import { memo, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import styles from "./Topbar.module.css";
import { ChevronDown, User, LogOut } from "lucide-react";
import { useTranslation } from "react-i18next";
import axios from "axios";

const BASE_URL = "https://admin.osleague.online/api";

const navLinks = [
  { to: "/dashboard", labelKey: "topbar.dashboard" },
  { to: "/dashboard/leaderboard", labelKey: "topbar.leaderboard" },
  { to: "/dashboard/predictions", labelKey: "topbar.predictions" },
  { to: "/dashboard/profile", labelKey: "topbar.profile" },
];

function Topbar() {
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();

  const [profileOpen, setProfileOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const currentLang = i18n.language?.startsWith("ar") ? "ar" : "en";

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem("token");

      if (token) {
        await axios.post(
          `${BASE_URL}/logout`,
          {},
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
      }
    } catch (error) {
      console.error("Logout error:", error);
    }

    localStorage.removeItem("token");

    setProfileOpen(false);

    window.location.href = "/login";
  };

  const handleLanguageChange = (nextLang) => {
    i18n.changeLanguage(nextLang);
    localStorage.setItem("lang", nextLang);
  };

  return (
    <>
      <header className={styles.topbar}>
        <div className={styles.mainRow}>
          <button
            className={styles.menuBtn}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="menu"
          >
            ☰
          </button>

          <div className={styles.brandSection}>
            <img
              src="/logo_os1.jfif"
              alt="logo"
              width="120"
              height="35"
              className={styles.logo}
            />
          </div>

          <nav className={styles.links} aria-label={t("topbar.navigation")}>
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/dashboard"}
                className={({ isActive }) =>
                  isActive
                    ? `${styles.navLink} ${styles.active}`
                    : styles.navLink
                }
              >
                {t(link.labelKey)}
              </NavLink>
            ))}
          </nav>

          <div className={styles.right}>
            <div className={styles.profileDropdown}>
              <button
                className={styles.iconBtn}
                onClick={() => setProfileOpen(!profileOpen)}
                aria-label={t("topbar.profile")}
              >
                <User size={18} />
              </button>

              {profileOpen && (
                <div className={styles.profileMenu}>
                  <button
                    onClick={() => {
                      navigate("/dashboard/profile");
                      setProfileOpen(false);
                    }}
                  >
                    <User size={16} /> {t("common.profile")}
                  </button>

                  <button onClick={handleLogout} className={styles.logout}>
                    <LogOut size={16} /> {t("common.logout")}
                  </button>
                </div>
              )}
            </div>

            <div className={`dropdown ${styles.langDropdown}`}>
              <button
                className={styles.langTrigger}
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                aria-label={t("topbar.languageMenu")}
              >
                <span>
                  {currentLang === "ar"
                    ? t("navbar.language_ar")
                    : t("navbar.language_en")}
                </span>
                <ChevronDown size={14} />
              </button>

              <ul
                className={`dropdown-menu dropdown-menu-end ${styles.langMenu}`}
              >
                <li>
                  <button
                    className={`${styles.langItem} ${
                      currentLang === "ar" ? styles.langItemActive : ""
                    }`}
                    onClick={() => handleLanguageChange("ar")}
                  >
                    {t("topbar.arabicOption")}
                  </button>
                </li>
                <li>
                  <button
                    className={`${styles.langItem} ${
                      currentLang === "en" ? styles.langItemActive : ""
                    }`}
                    onClick={() => handleLanguageChange("en")}
                  >
                    {t("topbar.englishOption")}
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </header>

      {menuOpen && (
        <div className={styles.mobileMenu}>
          <button
            className={styles.closeBtn}
            onClick={() => setMenuOpen(false)}
          >
            ✕
          </button>

          <nav className={styles.mobileNav}>
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/dashboard"}
                className={({ isActive }) =>
                  isActive
                    ? `${styles.mobileLink} ${styles.mobileLinkActive}`
                    : styles.mobileLink
                }
                onClick={() => setMenuOpen(false)}
              >
                {t(link.labelKey)}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </>
  );
}

export default memo(Topbar);
